import { useEffect, useRef } from 'react';
import { resolvePlayheadInitKey } from '@/lib/timeline/resolvePlayheadInitKey';

interface TimedInterval {
  id: number;
  startTime: number;
  endTime?: number;
}

/**
 * Seeks to the first interval only when the clip / first-scene identity
 * changes. Trimming start/end must not re-seek (that remounted the preview
 * on every pointermove while dragging the first interval). Used by the
 * editor's `Timeline` and by AI Studio, which has no timeline.
 *
 * `intervalsLoaded` is false on the render where a newly selected clip
 * still carries the previous clip's intervals: seeking then would land on
 * the old clip's start and, since the key is already spent, stay there.
 */
export function usePlayheadInit({
  intervals, selectedClip, onTimeChange, onSeekRequest, enabled = true, intervalsLoaded = true,
}: {
  intervals?: TimedInterval[] | null;
  selectedClip?: TimedInterval | null;
  onTimeChange?: (time: number) => void;
  onSeekRequest?: (time: number) => void;
  enabled?: boolean;
  intervalsLoaded?: boolean;
}) {
  const lastInitKeyRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (!enabled || !intervalsLoaded) return;
    const base = (intervals && intervals.length > 0)
      ? intervals
      : (selectedClip ? [selectedClip] : []);
    if (base.length === 0) return;

    const key = resolvePlayheadInitKey(base, selectedClip);
    if (lastInitKeyRef.current === key) return;
    lastInitKeyRef.current = key;

    const initTime = base[0].startTime ?? 0;
    onTimeChange?.(initTime);
    onSeekRequest?.(initTime);
  }, [enabled, intervalsLoaded, intervals, selectedClip, onTimeChange, onSeekRequest]);
}
